import { negotiationTermLabel } from '~/features/market/negotiations/sub-components/NegotiationTermsSummary';
import type { JOB_NEGOTIATION_TERM_KEYS } from '~/features/market/negotiations/utils';

export type ServiceTerm = (typeof JOB_NEGOTIATION_TERM_KEYS)[number];
export type TermSection = { title: string; terms: readonly ServiceTerm[] };
export type TermTab = { name: string; sections: readonly TermSection[] };

/**
 * Every term a counteroffer can propose, grouped on the job negotiation's tabs.
 * Terms those tabs never show (call shifts, RVU bonus, labor requirement and
 * periods) sit in the closest tab, so each editable term stays editable.
 */
export const SERVICE_TERM_TABS: readonly TermTab[] = [
  {
    name: 'Contract Overview',
    sections: [
      { title: 'Contract Dates', terms: ['startDate', 'endDate'] },
      {
        title: 'Staffing Requirements',
        terms: [
          'numOfShifts',
          'hoursPerShift',
          'laborReqType',
          'numRvusReq',
          'minRvusPerShift',
          'shiftPeriod',
          'shiftsPerPeriod',
          'callShiftsPerPeriod',
          'rvusPerPeriod',
          'rvuPeriod',
        ],
      },
    ],
  },
  {
    name: 'Shift Distribution',
    sections: [
      { title: 'Weekday Shifts', terms: ['numDayShifts', 'numEveShifts', 'numNightShifts'] },
      {
        title: 'Weekend Shifts',
        terms: ['numOfWeDayShifts', 'numOfWeEveShifts', 'numOfWeNightShifts'],
      },
      {
        title: 'Holiday Shifts',
        terms: ['numHolDayShifts', 'numHolEveShifts', 'numHolNightShifts'],
      },
      {
        title: 'Weekday Call Shifts',
        terms: ['numDayCallShifts', 'numEveCallShifts', 'numNightCallShifts'],
      },
      {
        title: 'Weekend Call Shifts',
        terms: ['numOfWeDayCallShifts', 'numOfWeEveCallShifts', 'numOfWeNightCallShifts'],
      },
      {
        title: 'Holiday Call Shifts',
        terms: ['numHolDayCallShifts', 'numHolEveCallShifts', 'numHolNightCallShifts'],
      },
    ],
  },
  {
    name: 'Compensation',
    sections: [
      {
        title: 'Base Compensation',
        terms: ['compType', 'pricePerHour', 'pricePerShift', 'pricePerRvu', 'pricePerCallShift'],
      },
      { title: 'RVU Bonus', terms: ['rvuBonusPeriod', 'rvuBonusMinimum', 'rvuBonusAmt'] },
      {
        title: 'Shift Differentials',
        terms: [
          'shiftDiffType',
          'eveShiftRateDiff',
          'nightShiftRateDiff',
          'weDayShiftRateDiff',
          'weEveShiftRateDiff',
          'weNightShiftRateDiff',
          'holDayShiftRateDiff',
          'holEveShiftRateDiff',
          'holNightShiftRateDiff',
        ],
      },
      {
        title: 'Call Shift Differentials',
        terms: [
          'callShiftDiffType',
          'eveCallShiftRateDiff',
          'nightCallShiftRateDiff',
          'weDayCallShiftRateDiff',
          'weEveCallShiftRateDiff',
          'weNightCallShiftRateDiff',
          'holDayCallShiftRateDiff',
          'holEveCallShiftRateDiff',
          'holNightCallShiftRateDiff',
        ],
      },
    ],
  },
  {
    name: 'Contract Terms',
    sections: [
      {
        title: 'Contract Terms and Conditions',
        terms: ['bonusesTxt', 'adminCostsTxt', 'infrastructureTxt', 'extraTxt'],
      },
    ],
  },
];

// The camel-case split in negotiationTermLabel reads badly for these terms.
const LABELS: Partial<Record<ServiceTerm, string>> = {
  numDayShifts: 'Weekday day shifts',
  numEveShifts: 'Weekday evening shifts',
  numNightShifts: 'Weekday night shifts',
  numOfWeDayShifts: 'Weekend day shifts',
  numOfWeEveShifts: 'Weekend evening shifts',
  numOfWeNightShifts: 'Weekend night shifts',
  numHolDayShifts: 'Holiday day shifts',
  numHolEveShifts: 'Holiday evening shifts',
  numHolNightShifts: 'Holiday night shifts',
  numDayCallShifts: 'Weekday day call shifts',
  numEveCallShifts: 'Weekday evening call shifts',
  numNightCallShifts: 'Weekday night call shifts',
  numOfWeDayCallShifts: 'Weekend day call shifts',
  numOfWeEveCallShifts: 'Weekend evening call shifts',
  numOfWeNightCallShifts: 'Weekend night call shifts',
  numHolDayCallShifts: 'Holiday day call shifts',
  numHolEveCallShifts: 'Holiday evening call shifts',
  numHolNightCallShifts: 'Holiday night call shifts',
  eveShiftRateDiff: 'Weekday evening differential',
  nightShiftRateDiff: 'Weekday night differential',
  weDayShiftRateDiff: 'Weekend day differential',
  weEveShiftRateDiff: 'Weekend evening differential',
  weNightShiftRateDiff: 'Weekend night differential',
  holDayShiftRateDiff: 'Holiday day differential',
  holEveShiftRateDiff: 'Holiday evening differential',
  holNightShiftRateDiff: 'Holiday night differential',
  eveCallShiftRateDiff: 'Weekday evening call differential',
  nightCallShiftRateDiff: 'Weekday night call differential',
  weDayCallShiftRateDiff: 'Weekend day call differential',
  weEveCallShiftRateDiff: 'Weekend evening call differential',
  weNightCallShiftRateDiff: 'Weekend night call differential',
  holDayCallShiftRateDiff: 'Holiday day call differential',
  holEveCallShiftRateDiff: 'Holiday evening call differential',
  holNightCallShiftRateDiff: 'Holiday night call differential',
};
export const serviceTermLabel = (term: ServiceTerm) => LABELS[term] ?? negotiationTermLabel(term);

const BLOCK_ELEMENTS = 'p, div, li, h1, h2, h3, h4, h5, h6, pre, blockquote';

// Contract text arrives as sanitised rich text; the comparison column shows it as plain text.
// Block and line boundaries become newlines (list items a "- " bullet, or "1. " numbers in an
// ordered list) so paragraphs do not run together; the column keeps them with
// `whitespace-pre-line`.
const listMarker = (item: Element) => {
  const list = item.parentElement;
  if (!(list instanceof HTMLOListElement)) return '- ';
  const items = Array.from(list.children).filter((child) => child.tagName === 'LI');
  return `${list.start + items.indexOf(item)}. `;
};

export const htmlToText = (html: string) => {
  const { body } = new DOMParser().parseFromString(html, 'text/html');
  body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  body.querySelectorAll('li').forEach((item) => item.prepend(listMarker(item)));
  body.querySelectorAll('li > ul, li > ol').forEach((list) => list.before('\n'));
  body.querySelectorAll(BLOCK_ELEMENTS).forEach((block) => block.append('\n'));
  return (body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
};
